import '@/styles/globals.css';
import '@/styles/responsive.css';
import '@rainbow-me/rainbowkit/styles.css';
import { WagmiProvider } from 'wagmi';
import { RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { AnimatePresence } from 'motion/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SessionProvider } from 'next-auth/react';
import Script from 'next/script';
import { Toaster } from 'sonner';
import { useEffect, useState } from 'react';
import { LitSessionProvider } from '@/contexts/LitSessionProvider';
import { ThemeProvider } from '@/contexts/ThemeProvider';
import { SwapProvider } from '@/contexts/SwapProvider';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import { wagmiConfig } from '@/config/wagmiConfig';
import Layout from '@/components/layout/Layout';
import { SearchHomeProvider } from '@/contexts/SearchHomeProvider';
import detectDeviceFromUserAgent from '@/lib/misc/detectUserAgent';
import { useIsMobile } from '@/hooks/utilities/use-mobile';
import type { AppProps } from 'next/app';
interface CustomAppProps extends AppProps {
  userAgent: string;
}
export const queryClient = new QueryClient();
export default function App({
  Component,
  pageProps,
  userAgent: context
}: CustomAppProps) {
  const isMobile = useIsMobile();
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
    global.userAgent = context;
    global.isMobile = (context as any).isMobile;
  }, [context]);
  if (!hasMounted) {
    return null;
  }
  return <>
      {isMobile ? <ErrorBoundary>
          <WagmiProvider reconnectOnMount={false} config={wagmiConfig}>
            <QueryClientProvider client={queryClient}>
              <RainbowKitProvider>
                <LitSessionProvider>
                  <SwapProvider>
                    <AnimatePresence initial={false} mode='popLayout'>
                      <ThemeProvider attribute='class' defaultTheme='dark' enableSystem={false}>
                        <SessionProvider>
                          <SearchHomeProvider>
                            <Layout>
                              <Component {...pageProps} />
                            </Layout>
                          </SearchHomeProvider>
                          {/* Google Analytics */}
                          <Script src='https://www.googletagmanager.com/gtag/js?id=G-QTG76SZSVV' strategy='afterInteractive' />
                          <Script id='google-analytics' strategy='afterInteractive'>
                            {`

          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'G-QTG76SZSVV');
        `}
                          </Script>
                        </SessionProvider>
                        <Toaster theme='dark' duration={5000} closeButton={true} position='top-center' visibleToasts={4} toastOptions={{
                      unstyled: true,
                      classNames: {
                        toast: 'p-4 w-[300px] flex items-center justify-center lg:mx-auto px-auto text-center text-sm bg-genius-indigo group toast group-[.toaster]:bg-genius-indigo group-[.toaster]:text-foreground genius-shadow border-[1px] border-genius-blue rounded-[10px]  flex gap-2 items-center p-2',
                        closeButton: ' border group-[.toast]:bg-genius-indigo group-[.toast]:hover:bg-genius-blue group-[.toast]:hover:border-genius-indigo border-genius-blue ',
                        error: 'text-red-400',
                        success: '',
                        warning: 'text-yellow-400'
                      }
                    }} />
                      </ThemeProvider>
                    </AnimatePresence>
                  </SwapProvider>
                </LitSessionProvider>

                <ReactQueryDevtools />
              </RainbowKitProvider>
            </QueryClientProvider>
          </WagmiProvider>
        </ErrorBoundary> : <WagmiProvider reconnectOnMount={false} config={wagmiConfig}>
          <QueryClientProvider client={queryClient}>
            <RainbowKitProvider>
              <LitSessionProvider>
                <SwapProvider>
                  <AnimatePresence initial={false} mode='popLayout'>
                    <ThemeProvider attribute='class' defaultTheme='dark' enableSystem={false}>
                      <SessionProvider>
                        <SearchHomeProvider>
                          <Layout>
                            <Component {...pageProps} />
                          </Layout>
                        </SearchHomeProvider>
                        {/* Google Analytics */}
                        <Script src='https://www.googletagmanager.com/gtag/js?id=G-QTG76SZSVV' strategy='afterInteractive' />
                        <Script id='google-analytics' strategy='afterInteractive'>
                          {`

          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'G-QTG76SZSVV');
        `}
                        </Script>
                      </SessionProvider>
                      <Toaster theme='dark' duration={5000} closeButton={true} position='top-center' visibleToasts={4} toastOptions={{
                    unstyled: true,
                    classNames: {
                      toast: 'p-4 w-[300px] flex items-center justify-center lg:mx-auto px-auto text-center text-sm bg-genius-indigo group toast group-[.toaster]:bg-genius-indigo group-[.toaster]:text-foreground genius-shadow border-[1px] border-genius-blue rounded-[10px]  flex gap-2 items-center p-2',
                      closeButton: ' border group-[.toast]:bg-genius-indigo group-[.toast]:hover:bg-genius-blue group-[.toast]:hover:border-genius-indigo border-genius-blue ',
                      error: 'text-red-400',
                      success: '',
                      warning: 'text-yellow-400'
                    }
                  }} />
                    </ThemeProvider>
                  </AnimatePresence>
                </SwapProvider>
              </LitSessionProvider>

              <ReactQueryDevtools />
            </RainbowKitProvider>
          </QueryClientProvider>
        </WagmiProvider>}
    </>;
}
App.getInitialProps = async appContext => {
  let pageProps = {};
  if (appContext.Component.getInitialProps) {
    pageProps = await appContext.Component.getInitialProps(appContext.ctx);
  }
  const {
    req
  } = appContext.ctx;
  const userAgent = req?.headers['user-agent'];
  const device = detectDeviceFromUserAgent(userAgent);
  global.userAgent = device;
  return {
    pageProps,
    userAgent: device
  };
};